// src/utils/sopsUi.js
// SOPS LIBRARY WEB VERIFIER: sopweb-bdb7afcd — SOPs Library, Seam 2 (website library screens).
//
// Pure helpers for the SOP screens (no Firebase imports, so sopsUi.test.js runs them without the app).
// The SERVER decides every save (functions/utils/sops.js + sopsService.js, sopslib-4c9e2a71); these
// only decide what the screens show. sopsUi.test.js requires the copies below to equal the server's.

import { orgYmd } from './orgTime'; // sopread-7e4c2a93 — deadlines and overdue days in the company's time zone
import { ROLES, FIELD_TECH_ROLES, isLeadHandRole } from './permissions'; // leadhand-9cb21d86

// ─── switch and roles ───────────────────────────────────────────────────────
// organizations/{org}.sopsLibraryEnabled — MISSING = OFF (opposite of catalogScreenEnabled). Only
// functions/scripts/setSopsLibrary.js sets it; firestore.rules keep it server-owned.
export const isSopsLibraryOn = (companySettings) => !!companySettings && companySettings.sopsLibraryEnabled === true;

// Copy of functions/utils/sops.js SOP_ROLES.MANAGE (= firestore.rules sopManagerRole()).
export const SOP_MANAGER_ROLES = Object.freeze(['Admin', 'Manager', 'Owner/System Admin', 'Owner / System Admin', 'Owner', 'System Admin']);
// Copy of functions/utils/sops.js SOP_GOD_MODE_UID (= firestore.rules isSupremeLeader()).
export const SOP_GOD_MODE_UID = 'wVkmpEsQkXdCAUiQ8QKgTG9Euw73';
const uidOf = (profile) => (profile && (profile.id || profile.uid)) || null;
export const isSopGodMode = (profile) => uidOf(profile) === SOP_GOD_MODE_UID;
export const isSopManagerRole = (role) => SOP_MANAGER_ROLES.includes(role);
export const canManageSops = (profile) => !!profile && (isSopManagerRole(profile.role) || isSopGodMode(profile));
/** Manage library (and every manager SOP page): the company's switch is exactly on AND Manager and up (or god mode). */
export const showSopsLibrary = (profile, companySettings) => isSopsLibraryOn(companySettings) && canManageSops(profile);

// sopread-7e4c2a93 (Seam 3) — "SOPs" in the menu and My SOPs: EVERYONE whose role can open SOPs.
// Copy of functions/utils/sops.js SOP_ROLES.ANY (sopsUi.test.js asserts they match). A new role is added
// there first. leadhand-9cb21d86 — the field roles (Field Technician, Lead Hand) come from permissions.js.
export const SOP_ANY_ROLES = Object.freeze([...SOP_MANAGER_ROLES, 'Office Staff', 'Sales Consultant', ...FIELD_TECH_ROLES]);
export const canUseSops = (profile) => !!profile && (SOP_ANY_ROLES.includes(profile.role) || isSopGodMode(profile));
// leadhand-9cb21d86 (Seam 5) — copy of functions/utils/sops.js SOP_ROLES.LEAD: a Lead Hand sees a view-only
// "Who's read it" list (listSopReaders) under each SOP in My SOPs. God mode is an Owner, so not this.
export const SOP_LEAD_ROLES = Object.freeze([ROLES.LEAD_HAND]);
export const showSopReaders = (profile) => !!profile && isLeadHandRole(profile.role);
/** The left-menu entry and the /sops route: the switch is exactly on AND the person's role can open SOPs. */
export const showSopsMenu = (profile, companySettings) => isSopsLibraryOn(companySettings) && canUseSops(profile);

// The three tabs of the SOPs page. Details / edit / new stay at /sops/<id>, /sops/<id>/edit, /sops/new.
export const SOPS_PATHS = Object.freeze({ toRead: '/sops', all: '/sops/all', library: '/sops/library' });
// sopremind-9c4e7a21 (Seam 4) — bells and emails link to /sops?open=<sopId> (staff have no per-SOP page):
// My SOPs selects that SOP. The summary email links to /sops/library?filter=overdue.
export const SOP_OPEN_PARAM = 'open';
export const LIBRARY_FILTER_PARAM = 'filter';

// ─── limits and choices (copies of functions/utils/sops.js) ─────────────────
export const LIMITS = Object.freeze({
    MAX_FILE_BYTES: 25 * 1024 * 1024,
    TITLE_MAX: 150,
    DESCRIPTION_MAX: 2000,
    CHANGE_NOTE_MAX: 500,
    FOLDER_NAME_MAX: 80,
    FOLDER_DEPTH_MAX: 5,
    CATALOG_IDS_MAX: 50,
    ACCESS_BATCH_MAX: 200,
});
export const DEADLINE_OPTIONS = Object.freeze([
    { value: 3, label: '3 days' }, { value: 7, label: '7 days' }, { value: 14, label: '14 days' },
    { value: 30, label: '30 days' }, { value: null, label: 'No deadline' },
]);
export const CONFIRM_OPTIONS = Object.freeze([
    { value: 'tap', label: 'Tap to confirm' },
    { value: 'sign', label: 'Type full name to sign' },
]);
export const REREAD_OPTIONS = Object.freeze([
    { value: null, label: 'Never' }, { value: 6, label: 'Every 6 months' }, { value: 12, label: 'Every 12 months' },
]);
// The server's refusal when an archived SOP's folder is archived (sops.REFUSE_RESTORE_FOLDER).
export const RESTORE_FOLDER_REFUSAL = 'Restore or choose a live folder first.';
export const isRestoreFolderRefusal = (err) => String((err && err.message) || '') === RESTORE_FOLDER_REFUSAL;

// ─── the file ───────────────────────────────────────────────────────────────
export function formatBytes(n) {
    if (typeof n !== 'number' || !Number.isFinite(n) || n < 0) return '';
    if (n < 1024) return `${n} bytes`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** A file someone picked → null when it can be sent, else what to do instead. */
export function pdfFileProblem(file) {
    if (!file) return 'Choose a PDF file.';
    if (!/\.pdf$/i.test(String(file.name || ''))) return 'Only PDF files can be added. Save it as a PDF and choose it again.';
    if (!(file.size > 0)) return 'This file is empty.';
    if (file.size > LIMITS.MAX_FILE_BYTES) return `This file is ${formatBytes(file.size)}. The limit is 25 MB.`;
    if (String(file.name).length > 200) return 'The file name is too long. Rename the file and choose it again.';
    return null;
}

/** The first bytes of the file → does it start like a PDF (%PDF-)? The server checks again. */
export function looksLikePdf(bytes) {
    if (!bytes || bytes.length < 5) return false;
    return String.fromCharCode(...Array.from(bytes.slice(0, 5))) === '%PDF-';
}

// ─── folders ────────────────────────────────────────────────────────────────
/** folders [{id,name,parentId,archived}] → Map(id → folder), every folder (archived too). */
export const foldersById = (folders) => new Map((Array.isArray(folders) ? folders : []).filter(Boolean).map((f) => [f.id, f]));

/** "Safety › Ladders" (loop-safe; works with archived folders too). */
export function folderPathText(byId, id) {
    const names = [];
    const seen = new Set();
    let cur = id;
    while (cur && byId.has(cur) && !seen.has(cur) && names.length < 25) {
        seen.add(cur);
        names.unshift(byId.get(cur).name || 'Folder');
        cur = byId.get(cur).parentId || null;
    }
    return names.join(' › ');
}

/** Live folders as a tree list, parents first: [{ id, name, depth, parentId }] (depth 0 = top). */
export function liveFolderList(folders) {
    const live = (Array.isArray(folders) ? folders : []).filter((f) => f && f.archived !== true);
    const ids = new Set(live.map((f) => f.id));
    const kids = new Map();
    for (const f of live) {
        const p = f.parentId && ids.has(f.parentId) ? f.parentId : null;
        if (!kids.has(p)) kids.set(p, []);
        kids.get(p).push(f);
    }
    for (const list of kids.values()) list.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    const out = [];
    const walk = (p, depth, seen) => {
        for (const f of kids.get(p) || []) {
            if (seen.has(f.id)) continue;
            out.push({ id: f.id, name: f.name, depth, parentId: f.parentId || null });
            walk(f.id, depth + 1, new Set([...seen, f.id]));
        }
    };
    walk(null, 0, new Set());
    return out;
}

/** The folder and every folder under it (any depth). */
export function folderAndBelow(folders, folderId) {
    const list = Array.isArray(folders) ? folders : [];
    const out = new Set([folderId]);
    for (let grew = true; grew;) {
        grew = false;
        for (const f of list) if (f && !out.has(f.id) && out.has(f.parentId)) { out.add(f.id); grew = true; }
    }
    return out;
}

// Mirrors of the server's folderDepth / subtreeHeight (a top-level folder is depth 1).
function depthOf(byId, id) {
    let d = 0; let cur = id; const seen = new Set();
    while (cur) { if (seen.has(cur) || !byId.has(cur)) return null; seen.add(cur); d++; cur = byId.get(cur).parentId || null; }
    return d;
}
function heightOf(folders, id, guard = 0) {
    if (guard > 25) return 1;
    const kids = folders.filter((f) => f.parentId === id);
    return 1 + kids.reduce((m, k) => Math.max(m, heightOf(folders, k.id, guard + 1)), 0);
}

/** Where a NEW folder may go: [{ id|null, label, depth }] — top level, then live folders under 5 deep. */
export function newFolderParents(folders) {
    const byId = foldersById(folders);
    return [{ id: null, label: 'Top level', depth: 0 },
        ...liveFolderList(folders).filter((f) => (depthOf(byId, f.id) || 99) < LIMITS.FOLDER_DEPTH_MAX).map((f) => ({ id: f.id, label: f.name, depth: f.depth + 1 }))];
}

/** Where a folder may MOVE to: not itself, not inside itself, and the result at most 5 deep. */
export function moveFolderTargets(folders, folderId) {
    const list = (Array.isArray(folders) ? folders : []).filter(Boolean);
    const byId = foldersById(list);
    const below = folderAndBelow(list, folderId);
    const h = heightOf(list, folderId);
    const current = byId.has(folderId) ? byId.get(folderId).parentId || null : null;
    const out = [];
    if (h <= LIMITS.FOLDER_DEPTH_MAX && current !== null) out.push({ id: null, label: 'Top level', depth: 0 });
    for (const f of liveFolderList(list)) {
        if (below.has(f.id) || f.id === current) continue;
        const d = depthOf(byId, f.id);
        if (d === null || d + h > LIMITS.FOLDER_DEPTH_MAX) continue;
        out.push({ id: f.id, label: f.name, depth: f.depth + 1 });
    }
    return out;
}

// ─── the library list ───────────────────────────────────────────────────────
export const LIBRARY_FILTERS = Object.freeze([
    { value: 'all', label: 'All' },
    { value: 'must', label: 'Must-read' },
    { value: 'archived', label: 'Archived' },
    { value: 'overdue', label: 'Overdue' }, // sopremind-9c4e7a21
]);
/** A ?filter= value from a link → one of the filters, else 'all'. */
export const libraryFilterFrom = (v) => (LIBRARY_FILTERS.some((f) => f.value === v) ? v : 'all');

/** The SOPs the table shows. sops: [{ title, titleLower, folderId, archived, mustReadCount }] */
export function filterSops(sops, { filter = 'all', folderIds = null, text = '' } = {}) {
    const q = String(text || '').trim().toLowerCase();
    return (Array.isArray(sops) ? sops : []).filter((s) => {
        if (!s) return false;
        if (filter === 'archived' ? s.archived !== true : s.archived === true) return false;
        if (filter === 'must' && !(s.mustReadCount > 0)) return false;
        if (filter === 'overdue' && !(s.overdueCount > 0)) return false; // sopremind-9c4e7a21
        if (folderIds && !folderIds.has(s.folderId || null)) return false;
        if (q && !String(s.titleLower || s.title || '').toLowerCase().includes(q)) return false;
        return true;
    }).sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
}

/**
 * sopremind-9c4e7a21 — the red banner: overdue reads across live SOPs (server-kept overdueCount on
 * each SOP — people whose overdue alert went out and who still haven't read it). → { reads, sops }
 */
export function overdueSummary(sops) {
    const live = (Array.isArray(sops) ? sops : []).filter((s) => s && s.archived !== true && s.overdueCount > 0);
    return { reads: live.reduce((n, s) => n + s.overdueCount, 0), sops: live.length };
}
export const overdueBannerText = ({ reads, sops }) => `${reads} overdue read${reads === 1 ? '' : 's'} across ${sops} SOP${sops === 1 ? '' : 's'} — only Managers and up see this`;

/** Per-folder counts for the tree (the folder and everything under it), for the SOPs given. */
export function folderCounts(folders, sops) {
    const list = Array.isArray(folders) ? folders : [];
    const out = new Map();
    for (const f of list) {
        const ids = folderAndBelow(list, f.id);
        out.set(f.id, (sops || []).filter((s) => ids.has(s.folderId)).length);
    }
    return out;
}

/** "Must read" when anyone must read it, else "Read as needed" (reading status itself = Seam 3). */
export const readTypeLabel = (sop) => (sop && sop.mustReadCount > 0 ? 'Must read' : 'Read as needed');

/** The "Who can see" number (server-kept count; '—' when it hasn't been counted yet). */
export const canSeeText = (sop) => (sop && typeof sop.canSeeCount === 'number' ? String(sop.canSeeCount) : '—');

export const deadlineText = (daysToRead) => (daysToRead ? `${daysToRead} days to read` : 'No deadline');

/** A Firestore Timestamp / Date → "Oct 21, 2026" ('' when missing). */
export function dateText(ts) {
    const d = ts && typeof ts.toDate === 'function' ? ts.toDate() : (ts instanceof Date ? ts : null);
    return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}

// ─── the People grid ────────────────────────────────────────────────────────
/**
 * Every active person in the company (the god-mode account left out) as grid rows.
 *   users: CrmApp's live list (active only). access: Map(uid → stored record) for an existing SOP.
 * → [{ uid, name, role, always, canSee, mustRead }]  (always = Owner/Admin/Manager: "Always" can see)
 */
export function peopleRows(users, access = new Map()) {
    return (Array.isArray(users) ? users : [])
        .filter((u) => u && u.id && u.id !== SOP_GOD_MODE_UID)
        .map((u) => {
            const always = isSopManagerRole(u.role);
            const rec = access.get(u.id);
            const mustRead = !!rec && rec.mustRead === true;
            return { uid: u.id, name: u.name || u.email || 'Unnamed', role: u.role || '', always, canSee: always || mustRead || (!!rec && rec.canSee === true), mustRead };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
}

/** Tick or untick one box. Must read turns Can see on; Can see off turns Must read off; managers always see. */
export function setTick(rows, uid, column, value) {
    return rows.map((r) => (r.uid === uid ? applyTick(r, column, value) : r));
}
function applyTick(r, column, value) {
    if (column === 'mustRead') return { ...r, mustRead: value, canSee: r.always || value || r.canSee };
    if (r.always) return r; // "Always" can't be unticked
    return { ...r, canSee: value, mustRead: value ? r.mustRead : false };
}
/** "Select all" for a column. */
export const setAllTicks = (rows, column, value) => rows.map((r) => applyTick(r, column, value));
export const allTicked = (rows, column) => rows.length > 0 && rows.every((r) => r[column]);

/** "5 can see · 2 must read" (managers count as can see — they always can). */
export function peopleCountLine(rows) {
    const see = rows.filter((r) => r.canSee).length;
    const must = rows.filter((r) => r.mustRead).length;
    return `${see} can see · ${must} must read`;
}

/**
 * What setSopAccess needs: only the people whose ticks differ from what is stored (or, for a new
 * SOP, anyone ticked — and managers only when they must read). → [{ uid, canSee, mustRead }]
 */
export function accessChanges(rows, access = new Map()) {
    const out = [];
    for (const r of rows) {
        const rec = access.get(r.uid);
        const was = { canSee: !!rec && rec.canSee === true, mustRead: !!rec && rec.mustRead === true };
        if (r.always) {
            if (r.mustRead !== was.mustRead) out.push({ uid: r.uid, canSee: true, mustRead: r.mustRead });
            continue;
        }
        if (r.canSee !== was.canSee || r.mustRead !== was.mustRead) out.push({ uid: r.uid, canSee: r.canSee, mustRead: r.mustRead });
    }
    return out;
}

/** Split into the server's 200-per-call limit. */
export function chunks(list, n = LIMITS.ACCESS_BATCH_MAX) {
    const out = [];
    for (let i = 0; i < list.length; i += n) out.push(list.slice(i, i + n));
    return out;
}

// ─── the details page ───────────────────────────────────────────────────────
/**
 * "People & reading": every stored record that can see it, with "no longer active" for anyone not in
 * the live team list. → [{ uid, name, access: 'Must read'|'Can see', deadlineAt, inactive }]
 */
export function readerRows(records, users) {
    const live = new Map((Array.isArray(users) ? users : []).map((u) => [u.id, u]));
    return (Array.isArray(records) ? records : [])
        .filter((r) => r && r.canSee === true && r.uid !== SOP_GOD_MODE_UID)
        .map((r) => {
            const u = live.get(r.uid);
            // sopread-7e4c2a93 — record: the stored access record, for readerStatus / lastOpenedText.
            return { uid: r.uid, name: (u && u.name) || r.userName || 'Unnamed', access: r.mustRead ? 'Must read' : 'Can see', deadlineAt: r.mustRead ? r.deadlineAt || null : null, inactive: !u, record: r };
        })
        .sort((a, b) => (a.inactive - b.inactive) || a.name.localeCompare(b.name));
}

/**
 * The details form's draft → updateSopDetails input with only what changed (null when nothing did).
 *   draft/original: { title, folderId, description, catalogServiceIds, daysToRead, rereadMonths, confirmType }
 */
export function detailChanges(draft, original) {
    const out = {};
    const norm = (k, v) => (k === 'catalogServiceIds' ? JSON.stringify(v || []) : k === 'title' || k === 'description' ? String(v || '').trim() : v === undefined ? null : v);
    for (const k of ['title', 'folderId', 'description', 'catalogServiceIds', 'daysToRead', 'rereadMonths', 'confirmType']) {
        if (norm(k, draft[k]) !== norm(k, original[k])) out[k] = k === 'title' || k === 'description' ? String(draft[k] || '').trim() : draft[k];
    }
    return Object.keys(out).length ? out : null;
}

/** The form's own check before Save. → null, or a plain sentence. */
export function detailsProblem(draft, { needsFile, fileProblem }) {
    if (needsFile && fileProblem) return fileProblem;
    if (!String(draft.title || '').trim()) return 'Give the SOP a title.';
    if (String(draft.title).trim().length > LIMITS.TITLE_MAX) return `The title can be up to ${LIMITS.TITLE_MAX} characters.`;
    if (!draft.folderId) return 'Choose a folder.';
    if (String(draft.description || '').length > LIMITS.DESCRIPTION_MAX) return `The description can be up to ${LIMITS.DESCRIPTION_MAX} characters.`;
    if ((draft.catalogServiceIds || []).length > LIMITS.CATALOG_IDS_MAX) return `Link at most ${LIMITS.CATALOG_IDS_MAX} catalog items.`;
    return null;
}

// ─── reading and confirming (sopread-7e4c2a93, Seam 3) ─────────────────────
// The SERVER decides every confirm (functions/utils/sopsService.js runConfirmRead). These only decide
// what the screens show; namesMatch / isReadFor are copies of functions/utils/sops.js and
// sopsUi.test.js runs both on the same table.

/** Copy of sops.normalizeName / sops.namesMatch — capitals and extra spaces ignored. */
export const normalizeName = (s) => String(s == null ? '' : s).normalize('NFC').replace(/\s+/g, ' ').trim().toLowerCase();
export const namesMatch = (typed, accountName) => {
    const a = normalizeName(accountName);
    return !!a && normalizeName(typed) === a;
};

/** Copy of sops.isReadFor — must read AND their last confirmation is the version they owe AND no re-read
 *  is owed (sopremind-9c4e7a21: rereadOwedSince = the re-read date came round). */
export const isReadFor = (r) => !!r && r.mustRead === true && !!r.requiredVersionId && r.lastConfirmedVersionId === r.requiredVersionId && !r.rereadOwedSince;

/** The version this record is about (Seam 3 copy, or the version owed on a record from before it). */
export const recordVersionId = (r) => (r && (r.currentVersionId || r.requiredVersionId)) || null;
/** Opened = the server stamped this exact version as handed to them (openSop) — and, for a re-read owed,
 *  opened again since it came due (the server checks the same: confirmSopRead, sopremind-9c4e7a21). */
const msOf = (ts) => (ts && typeof ts.toMillis === 'function' ? ts.toMillis() : ts && typeof ts.toDate === 'function' ? ts.toDate().getTime() : ts instanceof Date ? ts.getTime() : null);
export const hasOpenedCurrent = (r) => !!r && !!recordVersionId(r) && r.lastOpenedVersionId === recordVersionId(r)
    && (!r.rereadOwedSince || (msOf(r.lastOpenedAt) !== null && msOf(r.lastOpenedAt) >= msOf(r.rereadOwedSince)));

/** The person's own records worth showing: can see, SOP not archived. */
export const visibleRecords = (records) => (Array.isArray(records) ? records : []).filter((r) => r && r.canSee === true && r.sopArchived !== true);
/** Still to read: must read, not read for the version owed. */
export const owesReading = (r) => !!r && r.canSee === true && r.sopArchived !== true && r.mustRead === true && !isReadFor(r);
/** The menu badge: how many SOPs the person still has to read. */
export const toReadCount = (records) => (Array.isArray(records) ? records : []).filter(owesReading).length;

const toDate = (ts) => (ts && typeof ts.toDate === 'function' ? ts.toDate() : ts instanceof Date ? ts : null);
const ymdToUtc = (ymd) => { const [y, m, d] = String(ymd).split('-').map(Number); return Date.UTC(y, m - 1, d); };
/** "2026-11-05" → "Nov 5, 2026" (the date itself — no time zone shift). */
export function ymdText(ymd) {
    if (!ymd) return '';
    return new Date(ymdToUtc(ymd)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}
/** The deadline's day in the company's time zone, as "Nov 5, 2026". */
export const deadlineDayText = (deadlineAt, timeZone) => { const d = toDate(deadlineAt); return d ? ymdText(orgYmd(d, timeZone)) : ''; };
/** Whole days past the deadline's day, counted in the company's time zone (0 = due today or later). */
export function overdueDays(deadlineAt, now, timeZone) {
    const d = toDate(deadlineAt);
    if (!d || !(now instanceof Date) || now.getTime() <= d.getTime()) return 0;
    return Math.max(1, Math.round((ymdToUtc(orgYmd(now, timeZone)) - ymdToUtc(orgYmd(d, timeZone))) / 86400000));
}
const daysWord = (n) => `${n} day${n === 1 ? '' : 's'}`;

/**
 * One of the person's records → what the list badge and panel show.
 *   kind: 'read' | 'overdue' | 'due' | 'toRead' (must read, no deadline) | 'asNeeded'
 *   label: "Read" / "Overdue 3 days" / "Due Nov 5, 2026" / "To read" / "Read as needed"
 */
export function readingState(r, now, timeZone) {
    if (!r || r.mustRead !== true) return { kind: 'asNeeded', label: 'Read as needed' };
    if (isReadFor(r)) return { kind: 'read', label: 'Read' };
    if (!toDate(r.deadlineAt)) return { kind: 'toRead', label: 'To read' };
    const late = overdueDays(r.deadlineAt, now, timeZone);
    if (late > 0) return { kind: 'overdue', label: `Overdue ${daysWord(late)}`, days: late };
    return { kind: 'due', label: `Due ${deadlineDayText(r.deadlineAt, timeZone)}`, dueText: deadlineDayText(r.deadlineAt, timeZone) };
}
export const BADGE_CLASS = Object.freeze({
    read: 'bg-green-50 text-green-700 border-green-200',
    overdue: 'bg-red-50 text-red-700 border-red-200',
    due: 'bg-amber-50 text-amber-800 border-amber-200',
    toRead: 'bg-amber-50 text-amber-800 border-amber-200',
    asNeeded: 'bg-gray-100 text-gray-600 border-gray-200',
});

const byTitle = (a, b) => String(a.title || '').localeCompare(String(b.title || ''));
const deadlineMs = (r) => { const d = toDate(r.deadlineAt); return d ? d.getTime() : Infinity; };
/**
 * "To read": overdue first, then soonest due, then no deadline (each by title).
 *   keep: sopIds confirmed on this visit — they stay in place (showing the green box) until the page is left.
 */
export const toReadRows = (records, keep = null) => visibleRecords(records)
    .filter((r) => owesReading(r) || (!!keep && keep.has(r.sopId)))
    .sort((a, b) => (deadlineMs(a) - deadlineMs(b)) || byTitle(a, b));
/** "All my SOPs": still to read first (same order), then everything else by title. */
export function allMyRows(records) {
    const vis = visibleRecords(records);
    return [...toReadRows(vis), ...vis.filter((r) => !owesReading(r)).sort(byTitle)];
}

/** The green box after confirming: 'Read · version 2 · Nov 1, 2026' or 'Signed "Jo Smith" · version 2 · Nov 1, 2026'. */
export function confirmedText(r) {
    if (!r) return '';
    const n = r.lastConfirmedVersionNumber || r.currentVersionNumber || 1;
    const when = dateText(r.lastConfirmedAt);
    const head = r.lastConfirmedType === 'sign' && r.lastSignedName ? `Signed “${r.lastSignedName}”` : 'Read';
    return `${head} · version ${n}${when ? ` · ${when}` : ''}`;
}

/** Library "Read status": "1 of 3 read" (server-kept counts), or "Read as needed". */
export function readStatusText(sop) {
    if (!sop || !(sop.mustReadCount > 0)) return 'Read as needed';
    return `${typeof sop.readCount === 'number' ? sop.readCount : '—'} of ${sop.mustReadCount} read`;
}

/** Details "People & reading" → the Reading cell for one record: { text, tone }. */
export function readerStatus(r, now, timeZone) {
    if (!r || r.mustRead !== true) return { text: '—', tone: 'muted' };
    if (isReadFor(r)) {
        const n = r.lastConfirmedVersionNumber || 1;
        return { text: `${r.lastConfirmedType === 'sign' ? 'Signed' : 'Read'} v${n}${r.lastConfirmedAt ? ` · ${dateText(r.lastConfirmedAt)}` : ''}`, tone: 'ok' };
    }
    const earlier = r.lastConfirmedVersionNumber ? ` · last read v${r.lastConfirmedVersionNumber}` : '';
    const late = overdueDays(r.deadlineAt, now, timeZone);
    if (late > 0) return { text: `Overdue ${daysWord(late)}${earlier}`, tone: 'bad' };
    if (toDate(r.deadlineAt)) return { text: `Not read yet · due ${deadlineDayText(r.deadlineAt, timeZone)}${earlier}`, tone: 'warn' };
    return { text: `Not read yet${earlier}`, tone: 'warn' };
}
// ─── Who's read it (leadhand-9cb21d86, Seam 5) ─────────────────────────────
/**
 * One listSopReaders row → the pill: { kind, label } (kind picks the BADGE_CLASS colour).
 *   green "Read v2 · Nov 1, 2026" / "Signed v2 · …", amber "Due Nov 8, 2026", red "Overdue 3 days",
 *   grey "Read as needed"; amber "Not read yet" when it must be read but has no deadline.
 */
export function leadReaderPill(row, now, timeZone) {
    if (!row || row.mustRead !== true) return { kind: 'asNeeded', label: 'Read as needed' };
    if (row.read) {
        const when = row.confirmedAt ? dateText(new Date(row.confirmedAt)) : '';
        return { kind: 'read', label: `${row.confirmType === 'sign' ? 'Signed' : 'Read'} v${row.versionNumber || 1}${when ? ` · ${when}` : ''}` };
    }
    const deadline = row.deadlineAt ? new Date(row.deadlineAt) : null;
    if (!deadline) return { kind: 'toRead', label: 'Not read yet' };
    const late = overdueDays(deadline, now, timeZone);
    if (late > 0) return { kind: 'overdue', label: `Overdue ${daysWord(late)}` };
    return { kind: 'due', label: `Due ${deadlineDayText(deadline, timeZone)}` };
}
/** "2 of 3 read" — the must-read people shown (inactive people are left off by the server). */
export const leadReadersCountText = (res) => (res && res.mustReadCount > 0 ? `${res.readCount} of ${res.mustReadCount} read` : 'Read as needed');
export const LEAD_READERS_FOOTNOTE = 'View only. Managers set who must read it.';

/** "v2 · Nov 1, 2026", or "Never". */
export const lastOpenedText = (r) => (r && r.lastOpenedAt ? `v${r.lastOpenedVersionNumber || '?'} · ${dateText(r.lastOpenedAt)}` : 'Never');
